/**
 * Screen 4 — everything ever photographed.
 *
 * Filtering happens in the browser: a cellar is at most a couple of hundred
 * rows, they are all already here, and doing it locally means no index, no
 * round trip, and instant response as you type.
 */
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import CellarSummary from '../components/CellarSummary'
import Inventory from '../components/Inventory'
import { api } from '../lib/api'
import type { CellarWine } from '../lib/types'

const COLOURS = ['red', 'white', 'rosé', 'sparkling', 'fortified', 'orange']

export default function Cellar() {
  const query = useQuery({ queryKey: ['cellar'], queryFn: api.cellar })
  const [search, setSearch] = useState('')
  const [colour, setColour] = useState<string | null>(null)
  const [minStars, setMinStars] = useState(0)
  const [inStockOnly, setInStockOnly] = useState(false)

  const wines = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return (query.data?.wines ?? []).filter((wine) => {
      if (colour && (wine.colour ?? '').toLowerCase() !== colour) return false
      if (minStars && (wine.myRating ?? 0) < minStars) return false
      if (inStockOnly && !(wine.bottleCount ?? 0)) return false
      if (!needle) return true
      return [wine.displayName, wine.producer, wine.cuvee, wine.region, wine.vintage]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle)
    })
  }, [query.data, search, colour, minStars, inStockOnly])

  const all = query.data?.wines ?? []
  const total = all.length
  const bottles = all.reduce((sum, wine) => sum + (wine.bottleCount ?? 0), 0)

  return (
    <div className="space-y-4 pt-5">
      <h2 className="font-display text-2xl">Your cellar</h2>

      {/* The whole cellar, not the filtered view: the question this answers is
          "what have I got", and a search that narrows it to one bottle should
          not make the overview agree. */}
      <CellarSummary wines={all} />

      <input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Producer, region, vintage…"
        className="w-full rounded-full border border-white/10 bg-black/25 px-4 py-2.5 text-sm placeholder:text-stone-600 focus:border-wine-500/60 focus:outline-none"
      />

      <div className="flex flex-wrap gap-1.5">
        {COLOURS.map((option) => (
          <button
            key={option}
            onClick={() => setColour(colour === option ? null : option)}
            className={`chip ${colour === option ? 'chip-on' : ''}`}
          >
            {option}
          </button>
        ))}
        <button
          onClick={() => setMinStars(minStars === 4 ? 0 : 4)}
          className={`chip ${minStars ? 'chip-on' : ''}`}
        >
          ★ 4+
        </button>
        <button
          onClick={() => setInStockOnly(!inStockOnly)}
          className={`chip ${inStockOnly ? 'chip-on' : ''}`}
        >
          in stock
        </button>
      </div>

      {bottles > 0 && (
        <Link to="/ask" className="btn-ghost block w-full text-center">
          Ask the critics what to open →
        </Link>
      )}

      {query.isLoading && (
        <div className="grid grid-cols-2 gap-2">
          {[0, 1, 2, 3].map((index) => (
            <div key={index} className="card h-44 animate-pulse opacity-40" />
          ))}
        </div>
      )}

      {query.data && !total && (
        <p className="pt-10 text-center text-sm text-stone-400">
          Nothing here yet. Photograph a shelf and it will fill up.
        </p>
      )}

      {total > 0 && !wines.length && (
        <p className="pt-10 text-center text-sm text-stone-400">
          Nothing matches that.
        </p>
      )}

      <div className="grid grid-cols-2 gap-2">
        {wines.map((wine) => (
          <Tile key={wine.wineId} wine={wine} />
        ))}
      </div>
    </div>
  )
}

/**
 * The stepper sits outside the `<Link>` rather than inside it. Buttons nested
 * in an anchor are interactive content inside interactive content: the tap
 * navigates as well as counting, and every keyboard user meets the wine detail
 * page on their way to the minus sign.
 */
function Tile({ wine }: { wine: CellarWine }) {
  const name =
    wine.displayName ??
    [wine.producer, wine.cuvee, wine.vintage].filter(Boolean).join(' ') ??
    'A wine'
  const empty = !(wine.bottleCount ?? 0)

  return (
    <div className="card overflow-hidden transition hover:border-wine-600/50">
      <Link to={`/wine/${wine.wineId}`} className="block">
        <div className={`aspect-4/5 bg-cellar-700 ${empty ? 'opacity-40 grayscale' : ''}`}>
          {wine.cropUrl ? (
            <img src={wine.cropUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="grid h-full place-items-center text-3xl text-stone-700">🍷</div>
          )}
        </div>
        <div className="space-y-0.5 p-2.5">
          <h3 className="truncate text-sm font-medium">{name}</h3>
          <p className="truncate text-[11px] text-stone-500">
            {[wine.region, wine.colour].filter(Boolean).join(' · ')}
          </p>
          <p className="text-[11px] text-stone-600">
            {wine.myRating ? '★'.repeat(wine.myRating) : ''}
            {wine.sightingCount && wine.sightingCount > 1
              ? `${wine.myRating ? ' · ' : ''}seen ${wine.sightingCount}×`
              : ''}
          </p>
        </div>
      </Link>
      <Inventory
        wineId={wine.wineId}
        count={wine.bottleCount ?? 0}
        className="border-t border-white/5 px-2.5 py-2"
      />
      {/* Its own row rather than a button beside the stepper: at two columns
          on a phone there is no width for both, and this has to stay outside
          the tile's `<Link>` for the same reason the stepper does. */}
      <Link
        to={`/ask?wine=${wine.wineId}`}
        className="block border-t border-white/5 px-2.5 py-1.5 text-center text-[11px]
          text-stone-500 transition hover:text-wine-200"
      >
        Ask about this →
      </Link>
    </div>
  )
}
